import React, { useEffect, useState } from "react";
import {
  Check,
  ChevronRight,
  User,
  UserCog,
  Users,
  Store,
  MapPin,
  Grid3x3,
  DollarSign,
  UserCheck,
  Workflow,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import { Tooltip } from "react-tooltip";

// 9 Updated Steps
const stepInfo = [
  {
    key: "profile",
    title: "Profile Setup",
    desc: "Go to profile setup to set your company name, currency, and contact number. This ensures invoices and dashboards display your business identity correctly.",
    route: "/user-profile",
    icon: User,
  },
  {
    key: "roles",
    title: "Roles",
    desc: "Create roles to define different access levels for your team. For example, Manager, Valet, or Accountant.",
    route: "/staff-role-create",
    icon: UserCog,
  },
  {
    key: "rolesWithPermissions",
    title: "Assign Permission",
    desc: "Assign permissions to roles so your team can access only the modules they need — improving control and security.",
    route: "/assign-permission",
    icon: UserCog,
  },
  {
    key: "staff",
    title: "Staff",
    desc: "Add your staff members so they can log in and perform their duties within the system.",
    route: "/staff-create",
    icon: Users,
  },
  {
    key: "branches",
    title: "Branches",
    desc: "Create branches to represent different business units or parking sites managed by your company.",
    route: "/branch-create",
    icon: Store,
  },
  {
    key: "locations",
    title: "Locations",
    desc: "Add locations under each branch to map your physical parking areas or service points.",
    route: "/branch-location-create",
    icon: MapPin,
  },
  {
    key: "slots",
    title: "Slots",
    desc: "Define parking slots for each location to manage real-time parking capacity and vehicle assignment.",
    route: "/slots",
    icon: Grid3x3,
  },
  {
    key: "vehiclePricing",
    title: "Vehicle Pricing",
    desc: "Set vehicle pricing by type (car, bike, SUV) so the system can auto-calculate payments during checkout",
    route: "/vehicle-pricing",
    icon: DollarSign,
  },
  {
    key: "staffAssignedToBranches",
    title: "Assign Staff",
    desc: "Assign staff to specific branches so they can manage vehicles and customers at their site only.",
    route: "/staff-branch-assign",
    icon: UserCheck,
  },
  {
    key: "workflowsAssignedToLocations",
    title: "Assign Workflow",
    desc: "Assign workflows to locations to automate steps like check-in, parking, and checkout.",
    route: "/workflow-assign",
    icon: Workflow,
  },
  {
    key: "staffAssignedToSteps",
    title: "Workflow steps assign",
    desc: "Assign staff to workflow steps to ensure each task (like receiving, parking, or returning) is handled by the right person.",
    route: "/workflows",
    icon: Workflow,
  },
];

const ProgressBar = () => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({
    profile: false,
    roles: false,
    rolesWithPermissions: false,
    staff: false,
    branches: false,
    locations: false,
    slots: false,
    vehiclePricing: false,
    staffAssignedToBranches: false,
    workflowsAssignedToLocations: false,
    staffAssignedToSteps: false,
  });

  const navigate = useNavigate();

  // useEffect(() => {
  //   const fetchStatus = async () => {
  //     try {
  //       setLoading(true);
  //       const res = await apiCall("get", "/get-client-widget-data");
  //       const data = res.data.data;

  //       setStatus({
  //         profile: data.profile_setup?.has || false,
  //         roles: data.roles?.has || false,
  //         staff: data.staff?.has || false,
  //         branches: data.branches?.has || false,
  //         locations: data.locations?.has || false,
  //         slots: data.slots?.has || false,
  //         vehiclePricing: data.vehiclePricing?.has || false,
  //         staffAssignedToBranches: data.staffAssignedToBranches?.has || false,
  //         workflowsAssignedToLocations: data.workflowsAssignedToLocations?.has || false,
  //         staffAssignedToSteps: data.staffAssignedToSteps?.has || false,
  //       });
  //     } catch (error) {
  //       console.error("Failed to fetch setup status:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchStatus();
  // }, []);

  useEffect(() => {
    const fetchStatus = async () => {
      setLoading(true);

      // 1️⃣ Try to get cached status
      const cachedStatusStr = localStorage.getItem("progressStatus");
      const cachedStatus = cachedStatusStr ? JSON.parse(cachedStatusStr) : {};

      // 2️⃣ Check if there are incomplete steps
      const incomplete = Object.values(cachedStatus).some((v) => !v);

      if (cachedStatusStr && !incomplete) {
        // All steps completed, no need to call API
        setStatus(cachedStatus);
        setLoading(false);
        return;
      }

      // 3️⃣ Otherwise, fetch from API
      try {
        const res = await apiCall("get", "/get-client-widget-data");
        const data = res.data.data;

        const newStatus = {
          profile: data.profile_setup?.has || false,
          roles: data.roles?.has || false,
          rolesWithPermissions: data.rolesWithPermissions?.has || false,
          staff: data.staff?.has || false,
          branches: data.branches?.has || false,
          locations: data.locations?.has || false,
          slots: data.slots?.has || false,
          vehiclePricing: data.vehiclePricing?.has || false,
          staffAssignedToBranches: data.staffAssignedToBranches?.has || false,
          workflowsAssignedToLocations:
            data.workflowsAssignedToLocations?.has || false,
          staffAssignedToSteps: data.staffAssignedToSteps?.has || false,
        };

        setStatus(newStatus);

        // Save to localStorage
        localStorage.setItem("progressStatus", JSON.stringify(newStatus));
      } catch (error) {
        console.error("Failed to fetch setup status:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);
  const handleStepClick = (route) => {
    if (route === "/workflows") {
      navigate(`${route}`, {
        state: { progressBarNavigate: true,fromWorkflowListGuide:true },
      });
    } else {
      navigate(route, {
        state: { progressBarNavigate: true },
      });
    }
  };

  const completedCount = Object.values(status).filter(Boolean).length;
  const totalSteps = stepInfo.length;
  const currentStepIdx = stepInfo.findIndex((s) => !status[s.key]);
  const remainingCount = totalSteps - completedCount;

  if (loading) return null;
  //  if (loading) {
  //   return (
  //     <>
  //       <style jsx>{`
  //         .setup-progress-banner {
  //           background: linear-gradient(90deg, #f8fafc 25%, #e2e8f0 50%, #f8fafc 75%);
  //           border-left: 4px solid var(--primary-600);
  //           border-radius: 8px;
  //           padding: 1rem 1.5rem;
  //           margin: 1rem 1.5rem;
  //           box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
  //             0 4px 6px -2px rgba(0, 0, 0, 0.05);
  //         }
  //         [data-theme="dark"] .setup-progress-banner {
  //           background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  //           border-left-color: var(--primary-600);
  //         }
  //         @media (max-width: 768px) {
  //           .setup-progress-banner {
  //             padding: 0.875rem 1rem;
  //             margin: 1rem;
  //           }
  //         }
  //         .banner-shimmer {
  //           height: 140px;
  //           background: linear-gradient(90deg, #f8fafc 25%, #e2e8f0 50%, #f8fafc 75%);
  //           background-size: 200% 100%;
  //           animation: shimmer 1.5s infinite;
  //           border-radius: 8px;
  //         }
  //         @keyframes shimmer {
  //           0% { background-position: 200% 0; }
  //           100% { background-position: -200% 0; }
  //         }
  //       `}</style>
  //       <div className="setup-progress-banner banner-shimmer"></div>
  //     </>
  //   );
  // }
  if (completedCount === totalSteps) {
    return null;
  }

  return (
    <>
      <style jsx>{`
        /* Main Banner */
        .setup-progress-banner {
          background: var(--white);
          border-left: 6px solid var(--primary-600); /* Border thickness increased for style */
          border-radius: 16px; /* Increased border radius */
          padding: 1.5rem;
          margin: 1rem;
          /* Enhanced Modern Shadow */
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1),
            0 5px 15px rgba(0, 0, 0, 0.05);
          font-family: inherit;
        }

        .banner-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .banner-header .banner-desc {
          font-size: 1rem;
          color: var(--primary-600);
          font-weight: 600;
          margin: 0;
        }

        /* Summary + Button */
        .banner-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .progress-summary {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          font-weight: 600;
        }

        .progress-item {
          text-align: center;
          min-width: 60px;
        }

        .count {
          font-size: 1.75rem;
          font-weight: 700;
        }

        .text-success {
          color: #03db39ff !important;
        }
        .text-warning {
          color: #f59e0b;
        }
        .text-primary {
          color: #2563eb;
        }

        .label {
          font-size: 0.8rem;
          color: #64748b;
          margin-top: 0.25rem;
        }

        .divider {
          font-size: 1.5rem;
          color: #94a3b8;
          font-weight: 300;
        }

        .next-step-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: var(--primary-600);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
          box-shadow: 0 4px 10px var(--primary-600); /* Default shadow */
        }

        .next-step-btn:hover {
          background: var(--primary-600);
          transform: translateY(-2px); /* Increased lift */
          box-shadow: 0 8px 20px var(--primary-600); /* Stronger glow/shadow */
        }

        .next-icon {
          width: 18px;
          height: 18px;
        }

        /* Scrollable Steps */
        .progress-steps-wrapper {
          position: relative;
          width: 100%;
        }

        .progress-steps-container {
          display: flex;
          gap: 2rem;
          overflow-x: auto;
          scroll-behavior: smooth;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
          /* Minimal padding needed now, since tooltip is ported */
          padding: 1.5rem 0.5rem 1.5rem;
        }

        .progress-steps-container::-webkit-scrollbar {
          height: 6px;
        }

        .progress-steps-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .progress-steps-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .progress-steps-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .step-item {
          flex: 0 0 auto;
          width: 140px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        .step-circle {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: #f1f1f1ff;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          cursor: pointer;
          border: 3px solid transparent;
          position: relative;
          z-index: 2;
        }

        .step-circle .icon {
          width: 28px;
          height: 28px;
          color: #64748b;
        }

        .step-circle.completed {
          background: #dcfce7;
          border-color: #03db39ff;
        }

        .step-circle.completed .icon {
          color: #16a34a;
        }

        .step-circle.active {
          background: #dbeafeaa;
          border-color: red;
          /* Static ring before animation starts */
          box-shadow: 0 0 0 6px rgba(37, 99, 235, 0.1);
          animation: pulse 2s infinite;
        }

        .step-circle.active .icon {
          color: red;
        }

        .step-circle:hover:not(.completed) {
          background: #cbd5e1;
          transform: scale(1.05);
        }

        .step-label {
          margin-top: 1rem;
          font-size: 0.85rem;
          font-weight: 600;
          // color: #475569;
          color: var(--text-secondary-light);
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: 100%;
        }

        .step-connector {
          position: absolute;
          top: 35px;
          left: calc(50% + 35px);
          right: calc(-50% + 35px);
          height: 4px; /* Line thickness increased */
          background: #cbd5e1;
          z-index: 1;
        }

        .step-connector.completed {
          background: #16a34a;
        }

        /* --- REACT TOOLTIP CUSTOM STYLES --- */
        .my-custom-tooltip {
          background: #1e293b;
          color: white;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          white-space: normal;
          width: 220px;
          text-align: center;
          z-index: 1000;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          line-height: 1.5;
        }

        .my-custom-tooltip.react-tooltip-place-top:after {
          border-top-color: #1e293b;
        }
        .my-custom-tooltip .tooltip-title {
          display: block;
          font-weight: 700;
          font-size: 15px;
          margin-bottom: 6px;
          color: white;
          line-height: 1.3;
        }

        .my-custom-tooltip .tooltip-desc {
          display: block;
          font-size: 13px;
          color: #e2e8f0;
          font-weight: 400;
          line-height: 1.4;
        }

        @keyframes pulse {
          0%,
          100% {
            box-shadow: 0 0 0 0 var(--primary-100);
          }
          50% {
            box-shadow: 0 0 0 8px var(--primary-50);
          }
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .step-item {
            width: 120px;
          }

          .step-circle {
            width: 60px;
            height: 60px;
          }

          .step-circle .icon {
            width: 24px;
            height: 24px;
          }

          .step-connector {
            top: 30px;
            left: calc(50% + 30px);
            right: calc(-50% + 30px);
          }
        }

        @media (max-width: 768px) {
          .setup-progress-banner {
            padding: 1.25rem;
            margin: 0.75rem;
          }

          .banner-summary {
            flex-direction: column;
            align-items: stretch;
            gap: 1.25rem;
          }

          .progress-summary {
            justify-content: center;
            gap: 1.25rem;
          }

          .next-step-btn {
            width: 100%;
            justify-content: center;
          }

          .progress-steps-container {
            gap: 1.5rem;
            padding: 1.25rem 0.5rem 1.25rem;
          }

          .step-item {
            width: 100px;
          }

          .step-circle {
            width: 55px;
            height: 55px;
          }

          .step-circle .icon {
            width: 22px;
            height: 22px;
          }

          .step-label {
            font-size: 0.8rem;
            margin-top: 0.75rem;
          }

          .step-connector {
            left: calc(50% + 27px);
            right: calc(-50% + 27px);
          }
        }

        @media (max-width: 480px) {
          .progress-steps-container {
            gap: 1rem;
          }

          .step-item {
            width: 85px;
          }

          .step-circle {
            width: 50px;
            height: 50px;
          }

          .step-circle .icon {
            width: 20px;
            height: 20px;
          }
        }
      `}</style>

      <div className="setup-progress-banner">
        <div className="banner-content">
          {/* Header */}
          <div className="banner-header">
            <p className="banner-desc">
              🎯 Finish these <strong>{totalSteps}</strong> steps to Start your
              parking system.
            </p>
          </div>

          {/* Summary + Next Button */}
          <div className="banner-summary">
            <div className="progress-summary">
              <div className="progress-item">
                <span className="count text-success">{completedCount}</span>
                <div className="label">Complete</div>
              </div>
              <span className="divider">/</span>
              {remainingCount > 0 && (
                <>
                  {/* <span className="divider">·</span> */}
                  <div className="progress-item">
                    <span className="count text-danger">{remainingCount}</span>
                    <div className="label">In Complete</div>
                  </div>
                </>
              )}
            </div>

            {currentStepIdx !== -1 && (
              <button
                className="next-step-btn"
                onClick={() => handleStepClick(stepInfo[currentStepIdx].route)}
              >
                {stepInfo[currentStepIdx].title}
                <ChevronRight className="next-icon" />
              </button>
            )}
          </div>

          {/* Scrollable Steps */}
          <div className="progress-steps-wrapper">
            <div className="progress-steps-container">
              {stepInfo.map((step, idx) => {
                const completed = status[step.key];
                const active = idx === currentStepIdx;
                const isLast = idx === stepInfo.length - 1;
                const Icon = step.icon;

                return (
                  <div key={step.key} className="step-item">
                    <div
                      className={`step-circle ${completed ? "completed" : ""} ${
                        active ? "active" : ""
                      }`}
                      onClick={() => handleStepClick(step.route)}
                      // React Tooltip Attributes
                      data-tooltip-id="my-step-tooltip"
                      data-tooltip-place="top"
                      data-tooltip-html={`
                        <div>
                          <span class="tooltip-title">${step.title}</span>
                          <span class="tooltip-desc">${step.desc}</span>
                        </div>
                      `}
                    >
                      {completed ? (
                        <Check className="icon" />
                      ) : (
                        <Icon className="icon" />
                      )}
                    </div>
                    <div className="step-label">{step.title}</div>
                    {!isLast && (
                      <div
                        // Connector Logic Fix: Only checks if the current step is completed
                        className={`step-connector ${
                          completed ? "completed" : ""
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* --- REACT TOOLTIP PROVIDER (Portal) --- */}
      <Tooltip id="my-step-tooltip" className="my-custom-tooltip" />
    </>
  );
};

export default ProgressBar;